import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { X, Loader2, Upload, FileText, ListChecks, ChevronDown } from 'lucide-react';
import { useT } from '../../utils/translate';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

// Bitim summasi uchun maxsus kalit (backend utils/stageRules.js bilan bir xil)
export const VALUE_KEY = '__value';

export const flattenFields = (sections) => (sections || []).flatMap(s => s.fields || []);

// Backend "bosqich shartlari bajarilmagan" javobi
export const isRequirementsError = (err) => err?.response?.data?.code === 'stage_requirements';
export const requirementsOf = (err) => err?.response?.data?.missing || [];

/* ─── Sozlamalar: bosqich qoidalari (majburiy maydonlar + avtomatik vazifa) ─── */
export function StageRulesEditor({ stage, fields, users, onChange }) {
  const t = useT();
  const required = stage.requiredFields || [];
  const task = stage.autoTask || {};
  const [open, setOpen] = useState(required.length > 0 || !!task.enabled);
  const options = [{ id: VALUE_KEY, key: t('stageRules.valueField') }, ...fields];

  const toggleField = (id) => onChange('requiredFields', required.includes(id) ? required.filter(x => x !== id) : [...required, id]);
  const setTask = (patch) => onChange('autoTask', { ...task, ...patch });
  const summary = [
    required.length ? t('stageRules.requiredCount', { n: required.length }) : null,
    task.enabled ? t('stageRules.autoTaskOn') : null,
  ].filter(Boolean).join(' · ');

  return (
    <div className="pl-1">
      <button type="button" onClick={() => setOpen(v => !v)} className="flex items-center gap-1.5 text-xs text-ink-tertiary hover:text-primary-600">
        <ListChecks className="w-3.5 h-3.5" />
        <span>{t('stageRules.title')}</span>
        {summary && <span className="text-primary-600">— {summary}</span>}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="mt-2 space-y-3 rounded-lg bg-surface-50 p-2.5">
          <div>
            <p className="text-xs font-medium text-ink mb-1">{t('stageRules.requiredFields')}</p>
            <p className="text-xs text-ink-tertiary mb-1.5">{t('stageRules.requiredHint')}</p>
            <div className="flex flex-wrap gap-1.5">
              {options.map(f => {
                const on = required.includes(f.id);
                return (
                  <button key={f.id} type="button" onClick={() => toggleField(f.id)}
                    className={`px-2 py-0.5 text-xs rounded-full border transition-colors ${on ? 'bg-primary-500 border-primary-500 text-white' : 'bg-white border-surface-200 text-ink-secondary hover:border-primary-300'}`}>
                    {f.type === 'file' && <FileText className="inline w-3 h-3 mr-0.5 -mt-0.5" />}{f.key}
                  </button>
                );
              })}
              {fields.length === 0 && <span className="text-xs text-ink-tertiary italic">{t('stageRules.noFields')}</span>}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" className="accent-primary-600 w-3.5 h-3.5 rounded" checked={!!task.enabled}
                onChange={e => setTask({ enabled: e.target.checked })} />
              <span className="text-xs font-medium text-ink">{t('stageRules.autoTask')}</span>
            </label>
            {task.enabled && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
                <input className="input text-xs py-1 sm:col-span-3" placeholder={t('stageRules.taskTitle')}
                  value={task.title || ''} onChange={e => setTask({ title: e.target.value })} />
                <select className="input text-xs py-1" value={task.assignTo === 'user' ? (task.user || '') : 'deal'}
                  onChange={e => setTask(e.target.value === 'deal' ? { assignTo: 'deal', user: null } : { assignTo: 'user', user: e.target.value })}>
                  <option value="deal">{t('stageRules.toDealOwner')}</option>
                  {users.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
                </select>
                <label className="flex items-center gap-1.5 text-xs text-ink-tertiary sm:col-span-2 flex-wrap">
                  {t('stageRules.dueIn')}
                  <input type="number" min="0" className="input text-xs py-1 w-16" value={task.dueDays ?? 0}
                    onChange={e => setTask({ dueDays: Math.max(0, Number(e.target.value) || 0) })} />
                  {t('stageRules.days')}
                  <select className="input text-xs py-1 flex-1 min-w-[120px]" value={task.dueFrom || ''}
                    onChange={e => setTask({ dueFrom: e.target.value })}>
                    <option value="">{t('stageRules.fromEnterDay')}</option>
                    {fields.filter(f => f.type === 'date').map(f => (
                      <option key={f.id} value={f.id}>{t('stageRules.fromField', { name: f.key })}</option>
                    ))}
                  </select>
                </label>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Sozlamalar: muddat bo'yicha avtomatik bitim (masalan ikamet uzaytirish) ─── */
export function RenewalRuleCard({ rule, fields, funnels, onSave }) {
  const t = useT();
  const [form, setForm] = useState(() => ({ enabled: false, dateField: '', daysBefore: 30, funnel: '', stage: '', taskTitle: '', ...(rule || {}) }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const targetFunnel = funnels.find(f => String(f._id) === String(form.funnel));
  const dateFields = fields.filter(f => f.type === 'date');

  const save = async () => {
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-surface-200 rounded-xl p-4 space-y-3">
      <div>
        <h4 className="font-semibold text-ink text-sm">{t('stageRules.renewalTitle')}</h4>
        <p className="text-xs text-ink-tertiary mt-0.5">{t('stageRules.renewalHint')}</p>
      </div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" className="accent-primary-600 w-4 h-4 rounded" checked={!!form.enabled} onChange={e => set('enabled', e.target.checked)} />
        <span className="text-sm text-ink">{t('stageRules.renewalEnabled')}</span>
      </label>
      {form.enabled && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block"><span className="block text-xs font-medium text-ink mb-1">{t('stageRules.renewalDateField')}</span>
            <select className="input text-sm" value={form.dateField} onChange={e => set('dateField', e.target.value)}>
              <option value="">{t('stageRules.select')}</option>
              {dateFields.map(f => <option key={f.id} value={f.id}>{f.key}</option>)}
            </select></label>
          <label className="block"><span className="block text-xs font-medium text-ink mb-1">{t('stageRules.renewalDays')}</span>
            <input type="number" min="1" max="365" className="input text-sm" value={form.daysBefore} onChange={e => set('daysBefore', e.target.value)} /></label>
          <label className="block"><span className="block text-xs font-medium text-ink mb-1">{t('stageRules.renewalFunnel')}</span>
            <select className="input text-sm" value={form.funnel || ''} onChange={e => setForm(f => ({ ...f, funnel: e.target.value, stage: '' }))}>
              <option value="">{t('stageRules.select')}</option>
              {funnels.map(f => <option key={f._id} value={f._id}>{f.name}</option>)}
            </select></label>
          <label className="block"><span className="block text-xs font-medium text-ink mb-1">{t('stageRules.renewalStage')}</span>
            <select className="input text-sm" value={form.stage || ''} onChange={e => set('stage', e.target.value)} disabled={!targetFunnel}>
              <option value="">{t('stageRules.select')}</option>
              {(targetFunnel?.stages || []).map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
            </select></label>
          <label className="block sm:col-span-2"><span className="block text-xs font-medium text-ink mb-1">{t('stageRules.renewalTask')}</span>
            <input className="input text-sm" value={form.taskTitle || ''} onChange={e => set('taskTitle', e.target.value)} placeholder={t('stageRules.renewalTaskPlaceholder')} /></label>
        </div>
      )}
      <div className="flex justify-end">
        <button type="button" onClick={save} disabled={saving} className="btn-primary btn-sm flex items-center gap-1.5">
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}{t('stageRules.save')}
        </button>
      </div>
    </div>
  );
}

/* ─── Bitta maydon kiritish (fayl darhol yuklanadi) ─── */
function RequirementInput({ field, value, onChange }) {
  const t = useT();
  const [uploading, setUploading] = useState(false);

  if (field.type === 'file') {
    const upload = async (file) => {
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const r = await axios.post(`${API}/upload/file`, fd);
        onChange({ url: r.data.url, name: r.data.name });
      } catch (e) {
        toast.error(e.response?.data?.message || t('stageRules.uploadFailed'));
      } finally {
        setUploading(false);
      }
    };
    return value?.url ? (
      <div className="flex items-center gap-2 text-sm">
        <FileText className="w-4 h-4 text-ink-tertiary shrink-0" />
        <span className="truncate flex-1">{value.name}</span>
        <button type="button" onClick={() => onChange(null)} className="text-ink-tertiary hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
      </div>
    ) : (
      <label className="flex items-center gap-1.5 text-sm text-primary-600 cursor-pointer">
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        {t('stageRules.chooseFile')}
        <input type="file" className="hidden" accept="application/pdf,image/*" disabled={uploading}
          onChange={e => e.target.files?.[0] && upload(e.target.files[0])} />
      </label>
    );
  }
  if (field.type === 'boolean') {
    return (
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" className="accent-primary-600 w-4 h-4 rounded" checked={!!value} onChange={e => onChange(e.target.checked)} />
        {t('stageRules.yes')}
      </label>
    );
  }
  if (field.type === 'dropdown') {
    return (
      <select className="input text-sm" value={value || ''} onChange={e => onChange(e.target.value)}>
        <option value="">{t('stageRules.select')}</option>
        {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (field.type === 'multiselect') {
    const selected = Array.isArray(value) ? value : [];
    return (
      <div className="flex flex-wrap gap-1.5">
        {(field.options || []).map(o => (
          <button key={o} type="button" onClick={() => onChange(selected.includes(o) ? selected.filter(x => x !== o) : [...selected, o])}
            className={`px-2.5 py-0.5 text-xs rounded-full border ${selected.includes(o) ? 'bg-primary-500 border-primary-500 text-white' : 'border-surface-200 text-ink-secondary'}`}>
            {o}
          </button>
        ))}
      </div>
    );
  }
  if (field.type === 'textarea') {
    return <textarea rows={2} className="input text-sm" value={value || ''} onChange={e => onChange(e.target.value)} />;
  }
  return (
    <input className="input text-sm" value={value ?? ''}
      type={field.type === 'number' || field.id === VALUE_KEY ? 'number' : field.type === 'date' ? 'date' : 'text'}
      onChange={e => onChange(e.target.value)} />
  );
}

/* ─── Bosqichga o'tishda yetishmayotgan maydonlarni to'ldirish oynasi ─── */
// missing: [{ stageId, stageName, fieldId }]; onSubmit(customFieldValues, value) — o'tkazishni qayta urinadi
export function RequirementsModal({ open, missing, fields, values: initialValues, value: initialValue, busy, onSubmit, onClose }) {
  const t = useT();
  const [values, setValues] = useState({});
  const [value, setValue] = useState('');

  useEffect(() => {
    if (!open) return;
    setValues({ ...(initialValues || {}) });
    setValue(initialValue ? String(initialValue) : '');
  }, [open, initialValues, initialValue]);

  const byId = useMemo(() => Object.fromEntries(fields.map(f => [f.id, f])), [fields]);
  const groups = useMemo(() => {
    const map = new Map();
    for (const m of missing || []) {
      if (!map.has(m.stageId)) map.set(m.stageId, { name: m.stageName, items: [] });
      map.get(m.stageId).items.push(m.fieldId);
    }
    return [...map.values()];
  }, [missing]);
  const showStage = groups.length > 1;

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-modal w-full sm:max-w-md max-h-[90dvh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-surface-100 shrink-0">
          <div>
            <h2 className="font-semibold text-ink">{t('stageRules.modalTitle')}</h2>
            <p className="text-xs text-ink-tertiary mt-0.5">{t('stageRules.modalHint')}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-100 text-ink-tertiary"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-4">
          {groups.map((g, gi) => (
            <div key={gi} className="space-y-3">
              {showStage && <p className="text-xs font-semibold text-ink-tertiary uppercase tracking-wide">{g.name}</p>}
              {g.items.map(id => {
                const field = id === VALUE_KEY ? { id, key: t('stageRules.valueField'), type: 'number' } : byId[id];
                if (!field) return null;
                return (
                  <div key={id}>
                    <label className="block text-sm font-medium text-ink mb-1">{field.key}</label>
                    <RequirementInput field={field}
                      value={id === VALUE_KEY ? value : values[id]}
                      onChange={v => (id === VALUE_KEY ? setValue(v) : setValues(prev => ({ ...prev, [id]: v })))} />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-surface-100 shrink-0">
          <button onClick={onClose} className="btn-secondary btn-md">{t('stageRules.cancel')}</button>
          <button onClick={() => onSubmit(values, value === '' ? undefined : Number(value))} disabled={busy}
            className="btn-primary btn-md flex items-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}{t('stageRules.saveAndMove')}
          </button>
        </div>
      </div>
    </div>
  );
}
